import {
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
} from "@material-ui/core";
import { BaseTextFieldProps } from "@material-ui/core/TextField";
import { Clear, HighlightOff, MoreHoriz } from "@material-ui/icons";
import { addDays, parseISO } from "date-fns";
import React, { ReactElement, useEffect, useRef, useState } from "react";
import {
  Button,
  FieldTitle,
  InputHelperText,
  Record,
  ResettableTextField,
  ResourceContextProvider,
  RowClickFunction,
  Translate,
  useCheckMinimumRequiredProps,
  useDataProvider,
  useTranslate,
} from "react-admin";
import resourceNames from "../../../../common/data/resourceNames";
import { ViewEventDto } from "../../../../common/data/service-proxies";
import { DatePager } from "../../mui-inputs/DatePager";
import { CompactListBase } from "../CompactListBase";
import classes from "../DriverPicker.module.css";
import { EventPickerDataGrid } from "./EventPickerDataGrid";

interface Props extends BaseTextFieldProps {
  label?: string | ReactElement | false;
  resource?: string;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  touched?: boolean;
  error?: any;
  submitError?: any;
  helperText?: string;
  options?: any;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  checked?: boolean;
  multiple?: boolean;
  value?: any;
  initialDate?: Date;
  driverId: number;
  timeZone?: string;
}

const formatDisplayValue = (
  event: ViewEventDto,
  translate: Translate,
  timeZone?: string
): string => {
  return (
    `#${event.number} ` +
    translate(`appData.dutyCycles.${event.dutyStatus}`) +
    " at " +
    parseISO(event.startTime!).toLocaleTimeString("en-US", {
      timeZone,
    }) +
    " at " +
    event.locationDisplayName
  );
};

const EventPickerComponent = (props: Props, _: React.Ref<HTMLInputElement>) => {
  useCheckMinimumRequiredProps("EventPicker", ["name"], props);
  const {
    label,
    options,
    resource,
    submitError,
    required,
    name,
    touched,
    error,
    helperText,
    initialDate = new Date(),
    driverId,
    timeZone,
    ...rest
  } = props;

  const [displayValue, setDisplayValue] = useState<string>("");
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [selectedDate, setSelectedDate] = useState<Date>(initialDate);

  const inputRef = useRef<HTMLInputElement>(null);
  const dataProvider = useDataProvider();
  const translate = useTranslate();

  useEffect(() => {
    if (!dataProvider) {
      return;
    }

    if (!rest.value) {
      return;
    }

    if (rest.value === "") {
      return;
    }

    if (isNaN(rest.value)) {
      return;
    }

    if (displayValue !== "") {
      return;
    }

    dataProvider
      .getOne<ViewEventDto & Record>(resourceNames.HOS_EVENTS_VIEW, {
        id: Number(rest.value),
      })
      .then((result) =>
        setDisplayValue(formatDisplayValue(result.data, translate, timeZone))
      )
      .catch((_) => setSelectedId(""));
  }, [dataProvider, rest.value, displayValue, timeZone, translate]);

  const handleFocus: React.FocusEventHandler<HTMLInputElement> = (event) => {
    if (rest.onFocus) {
      rest.onFocus(event);
    }

    if (displayValue === "") {
      handleDialogOpen();
      inputRef.current?.blur();
    }
  };

  const input = {
    onBlur: rest.onBlur,
    onChange: rest.onChange,
    onFocus: handleFocus,
    checked: rest.checked,
    multiple: rest.multiple,
  };

  const inputOptions = { ...options } || {};
  inputOptions.inputProps = { ...inputOptions.inputProps, readOnly: true };

  const getEndAdornment = () => {
    return (
      <InputAdornment position="end">
        <IconButton
          className={classes["clear-button"]}
          aria-label="Browse"
          title="Browse"
          disableRipple
          disabled={rest.disabled}
          onClick={() => handleDialogOpen()}
        >
          <MoreHoriz />
        </IconButton>
      </InputAdornment>
    );
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleDialogOpen = () => {
    setDialogOpen(true);
  };

  const setSelectedId = (id: number | string) => {
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
      window.HTMLInputElement.prototype,
      "value"
    )?.set;
    nativeInputValueSetter?.call(inputRef.current, id);
    const inputEvent = new Event("input", { bubbles: true });
    inputRef.current?.dispatchEvent(inputEvent);
  };

  const handleResetClick = () => {
    setDisplayValue("");
    setSelectedId("");
    handleDialogClose();
  };

  const handleRowClick: RowClickFunction = (id, basePath, record) => {
    const event = record as ViewEventDto;
    setDisplayValue(formatDisplayValue(event, translate, timeZone));
    setSelectedId(id);
    handleDialogClose();
    return Promise.resolve("");
  };

  return (
    <>
      <ResettableTextField
        label={
          label !== "" &&
          label !== false && (
            <FieldTitle
              label={label ?? undefined}
              source={name}
              resource={resource}
              isRequired={required}
            />
          )
        }
        error={!!(touched && (error || submitError))}
        helperText={
          <InputHelperText
            touched={touched ?? false}
            error={error || submitError}
            helperText={helperText}
          />
        }
        {...inputOptions}
        {...sanitizeInputRestProps(rest)}
        InputProps={{
          endAdornment: getEndAdornment(),
        }}
        value={displayValue}
        inputRef={inputRef}
        {...input}
      />
      <Dialog
        fullWidth
        maxWidth="lg"
        open={dialogOpen}
        onClose={handleDialogClose}
      >
        <ResourceContextProvider value={resourceNames.HOS_EVENTS_VIEW}>
          <CompactListBase
            perPage={5000}
            sort={{ field: "startTime", order: "asc" }}
            basePath="/"
            resource={resourceNames.HOS_EVENTS_VIEW}
            filter={{ logDate: selectedDate, driverId }}
          >
            <DialogTitle>
              <div className={classes["dialog-title"]}>
                Select an event
                <div className={classes["dialog-title-toolbar"]}>
                  <DatePager
                    value={selectedDate}
                    onSetCurrentDate={setSelectedDate}
                    previousOnClick={() =>
                      setSelectedDate(addDays(selectedDate, -1))
                    }
                    nextOnClick={() =>
                      setSelectedDate(addDays(selectedDate, 1))
                    }
                  />
                  <IconButton onClick={handleDialogClose}>
                    <Clear />
                  </IconButton>
                </div>
              </div>
            </DialogTitle>
            <DialogContent className={classes["dialog-content"]}>
              <Card>
                <EventPickerDataGrid
                  handleRowClick={handleRowClick}
                  timeZone={timeZone}
                />
              </Card>
            </DialogContent>
            <DialogActions>
              <div className={classes["dialog-actions"]}>
                <Button label="Reset selection" onClick={handleResetClick}>
                  <HighlightOff />
                </Button>
              </div>
            </DialogActions>
          </CompactListBase>
        </ResourceContextProvider>
      </Dialog>
    </>
  );
};

const sanitizeInputRestProps = ({
  afterSubmit,
  allowEmpty,
  allowNull,
  alwaysOn,
  basePath,
  beforeSubmit,
  component,
  data,
  defaultValue,
  error,
  format,
  formatOnBlur,
  formClassName,
  initialValue,
  initializeForm,
  input,
  isEqual,
  isRequired,
  label,
  limitChoicesToValue,
  locale,
  meta,
  multiple,
  name,
  options,
  optionText,
  optionValue,
  parse,
  record,
  ref,
  render,
  resource,
  source,
  submitError,
  subscription,
  textAlign,
  translate,
  translateChoice,
  type,
  validate,
  validateFields,
  value,
  ...rest
}: any) => rest;

export const EventPicker = React.forwardRef(EventPickerComponent);
